import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Badge, Label, useThemeMode } from "flowbite-react";
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { HiTrash, HiX } from "react-icons/hi";
import CustomButton from '../../components/common/CustomButton';
import UnsavedChangesIndicator from '../../components/common/UnsavedChangesIndicator';
import { Drawer } from "../../components/Modals";
import RoadmapDeleteModal from '../../components/Modals/RoadmapDeleteModal';
import RichTextEditor from '../../components/RichTextEditor';
import { CreateSmapPayload, StrategyEntityItemType, StrategyMapListItem, useStrategyMapService } from "../../services/strategyMap";
import './CreateStrategyMapDrawer.css';

import { FaPlusCircle } from 'react-icons/fa';
import { useSingleSmapFetch } from '../../hooks/Smap/useSingleSmap';
import { useProjectService, useRoadmapService } from '../../services';
import CreateWorkUnit, { CreateWorkUnitSuccessProps } from '../CreateWorkUnit/CreateWorkUnit';
import SelectEntities from '../SelectEntity/SelectEntities';
import { StrategyMapDependency, StrategyMapDependencyMapper } from '../StrategyMapDependencyMapper/StrategyMapDependencyMapper';
import MilestoneRow from './components/MilestoneRow';
import SmapDependencyCard from './components/SmapDependencyCard/SmapDependencyCard';
import SmapDrawerSkeleton from './components/SmapDrawerSkeleton';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`smap-tabpanel-${index}`}
    aria-labelledby={`smap-tab-${index}`}
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </div>
);

// --- Main Drawer Component ---

interface CreateStrategyMapDrawerProps {
  isVisible: boolean;
  onClose: () => void;
  strategyMapId?: string;
  // Minimal data props for UI display
  data?: any;
  loading?: boolean;
  onSuccess?: (strategyMapId: string) => void;
  defaultTab?: number;
}

const CreateStrategyMapDrawer: React.FC<CreateStrategyMapDrawerProps> = ({
  isVisible,
  onClose,
  strategyMapId,
  data: initialData = {},
  loading = false,
  onSuccess,
  defaultTab = 0
}) => {
  
  const [mode] = useThemeMode();
  
  const { createStrategyMap, updateStrategyMap, deleteStrategyMap } = useStrategyMapService();
  const { data: fetchedData, loading: isFetching } = useSingleSmapFetch(isVisible && strategyMapId ? strategyMapId : '', {cached: false});
  
  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    if (isVisible) {
      setActiveTab(defaultTab);
    }
  }, [isVisible, defaultTab]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateWorkUnit, setShowCreateWorkUnit] = useState(false);
  const tabsRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (fetchedData && tabsRef.current && activeTab >0) {
      // Small timeout to allow drawer internal animations or layout to settle
      setTimeout(() => {
        tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 500);
    }
  }, [isVisible, defaultTab, tabsRef.current]);

  // Form State
  const [data, setData] = useState<StrategyEntityItemType | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<StrategyMapListItem[]>([]);
  const [additionalSelectedIds, setAdditionalSelectedIds] = useState<{
    roadmap_ids: string[];
    project_ids: string[];
  }>({
    roadmap_ids: [],
    project_ids: [],
  });
  const [pinnedEntities, setPinnedEntities] = useState<StrategyMapListItem[]>([]);
  const [milestones, setMilestones] = useState<any[]>([]);
  const [dependencies, setDependencies] = useState<StrategyMapDependency[]>([]);

  useEffect(() => {
    if (isVisible) {
      if (strategyMapId && fetchedData) {
        setData(fetchedData);
        
        // Map entities
        if (fetchedData.entities) {
            // @ts-ignore - StrategyMapData to StrategyMapListItem mapping
            setSelectedEntities(fetchedData.entities);
            // @ts-ignore
            setPinnedEntities(fetchedData.entities);
        }

        // Map milestones with _uid
        if (fetchedData.milestones) {
            setMilestones(fetchedData.milestones.map((m: any) => ({
                ...m,
                _uid: m._uid || Math.random().toString(36).substr(2, 9)
            })));
        }

        // Map dependencies
        if (fetchedData.dependencies) {
            setDependencies(fetchedData.dependencies.map((d: any) => ({
                ...d,
                _uid: Math.random().toString(36).substr(2, 9)
            })));
        }
      } else if (!strategyMapId) {
        // New entry mode
        setData(null);
        setSelectedEntities([]);
        setPinnedEntities([]);
        setMilestones([]);
        setDependencies([]);
      }
      setActiveTab(0);
    }
  }, [strategyMapId, isVisible, fetchedData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    //@ts-ignore
    setData(prev => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (content: string) => {
    //@ts-ignore
    setData((prev) => ({ ...prev, description: content }));
  };



  const handleAddMilestone = () => {
    setMilestones(prev => [...prev, { name: '', start_date: '', color: '#3b82f6', _uid: Date.now().toString() }]);
  };

  const handleRemoveMilestone = (uid: string) => {
    setMilestones(prev => prev.filter(m => m._uid !== uid));
  };

  const handleMilestoneChange = (uid: string, field: string, value: string) => {
    setMilestones(prev => prev.map(m => m._uid === uid ? { ...m, [field]: value } : m));
  };



  const handleRemoveDependency = (id: string) => {
    setDependencies(prev => prev.filter(d => d.id !== id));
  };

  const handleSubmit = async () => {
    if (!data?.name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    const strippedDescription = data.description.replace(/<[^>]*>/g, '').trim();
    if (!strippedDescription) {
      toast.error('Please enter a description');
      return;
    }
    if (selectedEntities.length === 0) {
      toast.error('Please select at least one entity');
      return;
    }
    

    const payload: CreateSmapPayload = {
      name: data.name,
      description: data.description,
      entities: selectedEntities.map(e => ({
        id: e.id,
        type: e.type
      })),
      milestones: milestones.map(m => ({
        name: m.name,
        start_date: m.start_date,
        color: m.color
      })),
      dependencies: dependencies.map(d => ({
        id: d.id,
        type: d.type,
        dependency: d.dependency.map((dep: any) => ({
            id: dep.id,
            type: dep.type
        }))
      }))
    };

    setIsSubmitting(true);
    try {
      let r; 
      if (strategyMapId) {
        // TODO: Implement updateStrategyMap in service if needed
        await updateStrategyMap(strategyMapId, payload);
        toast.success('Strategy Map updated successfully');
      } else {
        r = await createStrategyMap(payload);
        toast.success('Strategy Map created successfully');

      }
      onClose();
      onSuccess?.(r?.strategymap_id ?? strategyMapId);
    } catch (error) {
      console.error('Error submitting Strategy Map:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const singularMainEntityType = "Strategy Map";
  const singularEntityType = "Work Items";
  const hasUnsavedChanges = useMemo(() => {
    // If we're loading specific data or it's not ready, no changes yet
    if (strategyMapId && (isFetching || !fetchedData)) {
      return false;
    }

    // New Entry Mode: any input is a "change"
    if (!strategyMapId) {
      const hasName = !!data?.name;
      const hasDesc = !!data?.description;
      const hasEntities = selectedEntities.length > 0;
      const hasMilestones = milestones.length > 0;
      const hasDependencies = dependencies.length > 0;
      return hasName || hasDesc || hasEntities || hasMilestones || hasDependencies;
    }

    // Edit Mode: Compare current state with fetchedData

    // 1. Name & Description
    const normalizeDescription = (desc: string) => {
      if (!desc) return "";
      try {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = desc;
        const textContent = tempDiv.textContent || tempDiv.innerText || "";
        let normalized = textContent.replace(/\s+/g, " ");
        normalized = normalized.replace(/\s+([,.!?;:])/g, "$1");
        return normalized.trim();
      } catch (error) {
        let normalized = desc.replace(/<[^>]*>/g, "");
        normalized = normalized.replace(/\s+/g, " ");
        normalized = normalized.replace(/\s+([,.!?;:])/g, "$1");
        return normalized.trim();
      }
    };

    const nameChanged = (data?.name || "") !== (fetchedData?.name || "");

    const currentDescRaw = data?.description || "";
    const initialDescRaw = fetchedData?.description || "";
    const currentDescNormalized = normalizeDescription(currentDescRaw);
    const initialDescNormalized = normalizeDescription(initialDescRaw);
    
    // Check for significant differences (ignoring minor HTML structure changes if text content matches)
    const descContentMatch = currentDescNormalized === initialDescNormalized;
    // Also check raw equality to catch some formatting changes if needed, but text content is usually sufficient for "unsaved" warning
    const descriptionChanged = !descContentMatch && currentDescRaw !== initialDescRaw;


    // 2. Entities (Projects)
    const currentEntityIds = selectedEntities.map((p: any) => p.id || p.project_id).sort();
    const initialEntityIds = (fetchedData?.entities || [])
      .map((p: any) => p.id || p.project_id)
      .sort();
    const entitiesChanged =
      JSON.stringify(currentEntityIds) !== JSON.stringify(initialEntityIds);


    // 3. Milestones
    // Map to comparable objects (exclude _uid)
    const currentMilestones = milestones.map(m => ({
        name: m.name,
        start_date: m.start_date,
        color: m.color
    })).sort((a,b) => a.name.localeCompare(b.name));

    const initialMilestones = (fetchedData?.milestones || []).map((m: any) => ({
        name: m.name,
        start_date: m.start_date,
        color: m.color
    })).sort((a: any, b: any) => a.name.localeCompare(b.name));
    
    const milestonesChanged = JSON.stringify(currentMilestones) !== JSON.stringify(initialMilestones);


    // 4. Dependencies
    // Use a simplified comparable structure
    const normalizeDependencies = (deps: any[]) => {
      return deps.map((dep) => ({
          project_to: dep.project_to?.value || dep.project_to, // Handle both object and value if differing structures
          // Normalize project_on list
          project_on: (dep.project_on || [])
            .map((p: any) => p.value || p.project_id || p) // Handle both SelectOption and ID
            .sort(),
          type: dep.type || 'finish_to_start'
        }))
        .sort((a, b) => String(a.project_to).localeCompare(String(b.project_to)));
    };

    const currentDepsNormalized = normalizeDependencies(dependencies);
    const initialDepsNormalized = normalizeDependencies(fetchedData?.dependencies || []);
    
    const dependenciesChanged = JSON.stringify(currentDepsNormalized) !== JSON.stringify(initialDepsNormalized);

    return (
      nameChanged ||
      descriptionChanged ||
      entitiesChanged ||
      milestonesChanged ||
      dependenciesChanged
    );
  }, [
    strategyMapId,
    data,
    selectedEntities,
    milestones,
    dependencies,
    fetchedData,
    isFetching,
  ]);
  

  const handleDeleteStrategyMap = async () => {
    if(!strategyMapId) return;
    await deleteStrategyMap(strategyMapId);
    window.location.replace("/strategymaps");
    onClose();
  }

  const [refreshKey, setRefreshKey] = useState(0);
  console.log("Entities", selectedEntities);

  const handleCreateWorkUnit = ()=>{
    setShowCreateWorkUnit(true);
  };
  const handleAddEntities = async (data: CreateWorkUnitSuccessProps)=>{

    if(!data.id) return;
    
    const type = data.type;
    const id = data.id;
    
    let item_data: any;

    // if(type === "feature" || type == "normal_epic"){
    //   const project = await fetchProjectById(id);
    //   item_data = project;
    // }
    // else if(type === "roadmap"){
    //   const roadmap = await fetchRoadmapById(id);
    //   item_data = roadmap;
    // }

    setRefreshKey(pv=>pv+1);

    
    const defined_type =
      type === "advanced_epic" ? "roadmap_ids" : "project_ids";

    setAdditionalSelectedIds((prev) => {
      return {
        ...prev,
        //@ts-ignore
        [defined_type]: [...(prev?.[defined_type] || []), id],
      };
    });


    
  }
  useEffect(() => {
    
  }, [])
  return (
    <>
      <Drawer isOpen={isVisible} onClose={onClose} position="right">
        {strategyMapId && isFetching ? (
          <SmapDrawerSkeleton />
        ) : (
          <div className="smap-drawer">
            {/* Header */}
            <header className="smap-drawer__header">
              <div className="smap-drawer__header-content">
                <div className="smap-drawer__title-container">
                  <h2 className="smap-drawer__title">
                    {strategyMapId ? "Update " : "New"} {singularMainEntityType}
                  </h2>
                  {strategyMapId &&
                    (fetchedData?.created_at || initialData?.created_at) && (
                      <p className="smap-drawer__subtitle">
                        Created on:{" "}
                        {new Date(
                          fetchedData?.created_at || initialData?.created_at,
                        ).toLocaleDateString()}
                      </p>
                    )}
                </div>

                <div className="smap-drawer__actions">
                  {strategyMapId && (
                    <button
                      type="button"
                      className="text-light-status-error-text hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 hover:bg-light-status-error-bg dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
                      onClick={() => handleDeleteStrategyMap()}
                    >
                      <HiTrash className="text-xl" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="smap-drawer__close-button"
                    onClick={onClose}
                  >
                    <HiX className="text-xl" />
                  </button>
                </div>
              </div>
            </header>

            {/* Form Body */}
            <div className="smap-drawer__content">
              <div className="smap-drawer__form-container">
                {/* Basic Fields */}
                <div className="space-y-4">
                  <div className="smap-drawer__input-group">
                    <Label className="smap-drawer__label">
                      {singularMainEntityType} name*
                    </Label>
                    <input
                      type="text"
                      name="name"
                      value={data?.name ?? ""}
                      onChange={handleInputChange}
                      className="smap-drawer__input"
                      placeholder={`${singularMainEntityType} Name`}
                    />
                  </div>
                  <div className="smap-drawer__input-group">
                    <Label className="smap-drawer__label">
                      {singularMainEntityType} Description*
                    </Label>
                    <div className="smap-drawer__editor-wrapper">
                      <RichTextEditor
                        content={data?.description ?? ""}
                        onChange={handleDescriptionChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Tabs Section */}
                <div className="smap-drawer__tabs-wrapper flex">
                  <Box
                    sx={{ borderBottom: 0 }}
                    className="text-light-text-primary dark:text-white w-fit"
                  >
                    <Tabs
                      value={activeTab}
                      onChange={(_, val) => setActiveTab(val)}
                      sx={{
                        "& .MuiTabs-indicator": {
                          backgroundColor: "#1d64f3",
                          height: 3,
                        },
                        "& .MuiTab-root": {
                          textTransform: "none",
                          fontWeight: 500,
                          color: "inherit",
                        },
                      }}
                    >
                      <Tab label={singularEntityType} />
                      {/* <Tab label="Dependencies" /> */}
                      {/* <Tab label="Milestones" /> */}

                      {/* <button
                        className="ml-auto mt-3 h-fit text-[12px] py-2 bg-slate-800 text-white px-5 rounded-full flex items-center gap-2 hover:bg-slate-700 transition-colors"
                        onClick={() => setShowCreateWorkUnit(true)}
                      >
                        <FaPlusCircle className="text-[12px]" />
                        Add Work Item
                      </button> */}
                    </Tabs>
                  </Box>
                  <button
                    className="ml-auto mt-3 h-fit text-[12px] py-2 bg-slate-800 text-white px-5 rounded-full flex items-center gap-2 hover:bg-slate-700 transition-colors"
                    onClick={() => handleCreateWorkUnit()}
                  >
                    <FaPlusCircle className="text-[12px]" />
                    Create Item
                  </button>
                </div>

                {/* Tab Panels */}
                <CustomTabPanel value={activeTab} index={0}>
                  <div ref={tabsRef}>
                    <SelectEntities
                      key={refreshKey}
                      defaultSelectedIds={{
                        roadmap_ids: additionalSelectedIds.roadmap_ids,
                        project_ids: additionalSelectedIds.project_ids,
                      }}
                      defaultSelectedItems={selectedEntities}
                      onChange={setSelectedEntities}
                      entityTypeLabel={singularEntityType}
                      pinnedItems={pinnedEntities}
                      onBeforeToggle={(item, isSelected) => {
                        // Only validate when deselecting (unlinking)
                        if (isSelected) {
                          const isDependent = dependencies.some((dep) => {
                            // Check if the item is the source of a dependency
                            if (dep.id === item.id) return true;

                            // Check if the item is a target of a dependency
                            if (
                              dep.dependency?.some((d: any) => d.id === item.id)
                            )
                              return true;

                            return false;
                          });

                          if (isDependent) {
                            const entityType =
                              item.entity_type === "epic" ? "Epic" : "Feature";
                            toast.error(
                              `This ${entityType} is linked to a dependency. Please remove the dependency before unlinking the ${entityType}.`,
                            );
                            return false; // Prevent toggle
                          }
                        }
                        return true; // Allow toggle
                      }}
                    />
                  </div>
                </CustomTabPanel>

                <CustomTabPanel value={activeTab} index={1}>
                  <div className="flex flex-col gap-6 px-3">
                    <StrategyMapDependencyMapper
                      data={selectedEntities}
                      onSave={(newDep: any) => {
                        setDependencies((prev) => [...prev, newDep]);
                      }}
                    />

                    {dependencies.length > 0 && (
                      <div className="mt-8 space-y-4 border-t border-light-border dark:border-hover-dark pt-6">
                        <div className="flex items-center justify-between">
                          <h4 className="text-base font-bold text-light-text-primary dark:text-white">
                            Active Dependencies
                          </h4>
                          <Badge color="blue">{dependencies.length}</Badge>
                        </div>
                        <div className="space-y-3">
                          {dependencies.map((d) => (
                            <SmapDependencyCard
                              key={d.id}
                              dependency={d}
                              onRemove={() => handleRemoveDependency(d.id)}
                              entityType={singularEntityType}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {dependencies.length === 0 && (
                      <div className="text-center py-10 border-2 border-dashed border-light-border dark:border-hover-dark rounded-xl opacity-50">
                        <p className="text-sm">No dependencies mapped yet.</p>
                        <p className="text-xs mt-1">
                          Select an item above to start.
                        </p>
                      </div>
                    )}
                  </div>
                </CustomTabPanel>

                <CustomTabPanel value={activeTab} index={2}>
                  <div className="flex flex-col gap-5 px-3">
                    <div className="flex border-b border-light-border dark:border-hover-dark pb-5">
                      <h3 className="text-lg font-bold text-light-text-primary dark:text-white">
                        Milestones
                      </h3>
                      <button
                        className="ml-auto text-primary-light text-sm hover:underline"
                        onClick={handleAddMilestone}
                      >
                        + Add
                      </button>
                    </div>
                    {milestones.map((m) => (
                      <MilestoneRow
                        key={m._uid}
                        data={m}
                        onChange={(field, value) =>
                          handleMilestoneChange(m._uid, field, value)
                        }
                        onRemove={() => handleRemoveMilestone(m._uid)}
                      />
                    ))}
                    {milestones.length === 0 && (
                      <div className="text-center py-4 text-xs opacity-60">
                        No milestones added yet.
                      </div>
                    )}
                  </div>
                </CustomTabPanel>
              </div>
            </div>

            {/* Footer */}
            <footer className="smap-drawer__footer">
              <CustomButton
                variant="cancel"
                onClick={onClose}
                className="smap-drawer__footer-button"
              >
                Cancel
              </CustomButton>
              <UnsavedChangesIndicator
                hasUnsavedChanges={hasUnsavedChanges}
                className="ml-auto"
              />
              <CustomButton
                onClick={handleSubmit}
                isLoading={isSubmitting}
                className={`smap-drawer__footer-button ${!hasUnsavedChanges ? "ml-auto" : ""}`}
              >
                {strategyMapId ? "Update" : "Add"}
              </CustomButton>
            </footer>
          </div>
        )}
      </Drawer>

      <RoadmapDeleteModal
        showModal={showDeleteModal}
        projectName={fetchedData?.name || initialData?.name}
        setShowModal={setShowDeleteModal}
        handleConfirm={() => setShowDeleteModal(false)}
      />

      {showCreateWorkUnit && (
        <CreateWorkUnit
          isVisible={showCreateWorkUnit}
          onClose={() => setShowCreateWorkUnit(false)}
          onSuccess={(newItem) => {
            if (newItem) handleAddEntities(newItem);
          }}
          preventRedirect={true}
        />
      )}
    </>
  );
};

export default CreateStrategyMapDrawer;
